/**
 * PageHashExtractor - Utility for extracting page hashes from PageLayout arrays
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for extracting and managing page hashes,
 * which are used for hash comparison during incremental pagination.
 */

import type { PageLayout } from '../../../core/types/Typesetter';

/**
 * Extract page hashes from an array of PageLayout objects
 * 
 * Creates a Map from page_index to page_hash for pages that have a hash.
 * Used for storing page hashes for early termination during incremental pagination.
 * 
 * @param pages - Array of PageLayout objects
 * @returns Map from page_index to page_hash
 */
export function extractPageHashes(pages: PageLayout[]): Map<number, string> {
  const pageHashes = new Map<number, string>();
  
  for (const page of pages) {
    if (page.page_hash) {
      pageHashes.set(page.page_index, page.page_hash);
    }
  }
  
  return pageHashes;
}

/**
 * Get the minimum page index from an array of pages
 * 
 * Finds the smallest page_index value in the array.
 * Useful for determining where to start cache invalidation.
 * 
 * @param pages - Array of PageLayout objects
 * @returns Minimum page index, or null if array is empty
 */
export function getMinPageIndex(pages: PageLayout[]): number | null {
  if (pages.length === 0) {
    return null;
  }
  
  return Math.min(...pages.map(p => p.page_index));
}

/**
 * Get the maximum page index from an array of pages
 * 
 * Finds the largest page_index value in the array.
 * Useful for determining the end of a page range.
 * 
 * @param pages - Array of PageLayout objects
 * @returns Maximum page index, or null if array is empty
 */
export function getMaxPageIndex(pages: PageLayout[]): number | null {
  if (pages.length === 0) {
    return null;
  }
  
  return Math.max(...pages.map(p => p.page_index));
}

